import type { OrderRow } from '../../api/amazonconnect';
import { windowAggregate, type WindowAggregate } from '../daily-brief/aggregateOrders';
import type { DayWindow } from '../daily-brief/dayWindows';
import { buildBusinessHealth } from '../daily-brief/healthScore';
import { daysInWindow } from '../executive-kpis/periodWindows';
import type {
  MasterOverlayModel,
  OverlayMetricDef,
  OverlayMetricId,
  OverlayPoint,
} from './types';

export const OVERLAY_METRICS: OverlayMetricDef[] = [
  {
    id: 'gmv',
    label: 'GMV',
    color: 'var(--shell-accent)',
    axis: 'money',
    available: true,
    defaultOn: true,
  },
  {
    id: 'orders',
    label: 'Order items',
    color: '#38bdf8',
    axis: 'count',
    available: true,
    defaultOn: true,
  },
  {
    id: 'units',
    label: 'Units',
    color: '#a78bfa',
    axis: 'count',
    available: true,
    defaultOn: false,
  },
  {
    id: 'aov',
    label: 'AOV',
    color: '#34d399',
    axis: 'money',
    available: true,
    defaultOn: false,
  },
  {
    id: 'asp',
    label: 'ASP',
    color: '#fbbf24',
    axis: 'money',
    available: true,
    defaultOn: false,
  },
  {
    id: 'health',
    label: 'Business Health',
    color: '#4ade80',
    axis: 'score',
    available: true,
    defaultOn: false,
  },
  {
    id: 'confidence',
    label: 'Data Confidence',
    color: '#94a3b8',
    axis: 'score',
    available: true,
    defaultOn: false,
  },
  {
    id: 'returns',
    label: 'Returns',
    color: '#fb7185',
    axis: 'count',
    available: false,
    disabledReason: 'Returns domain not certified for overlay series',
  },
  {
    id: 'traffic',
    label: 'Traffic',
    color: '#22d3ee',
    axis: 'count',
    available: false,
    disabledReason: 'Sales & Traffic not certified LIVE for dashboard series',
  },
  {
    id: 'brand',
    label: 'Brand Analytics',
    color: '#e879f9',
    axis: 'count',
    available: false,
    disabledReason: 'Brand Analytics not certified LIVE',
  },
  {
    id: 'ads',
    label: 'Ad spend',
    color: '#f97316',
    axis: 'money',
    available: false,
    disabledReason: 'Advertising spend series not certified LIVE',
  },
];

function aspOf(a: WindowAggregate): number | null {
  if (a.sales == null || a.units == null || a.units <= 0) return null;
  return a.sales / a.units;
}

function resolveDay(
  orders: OrderRow[],
  day: DayWindow,
  dayAggs?: Record<string, WindowAggregate>,
): WindowAggregate {
  if (dayAggs) {
    return (
      dayAggs[day.isoFrom] ?? {
        orderCount: 0,
        ordersWithTotal: 0,
        sales: 0,
        currencies: [],
        units: 0,
        ordersWithUnits: 0,
        unshipped: 0,
        canceled: 0,
        aov: 0,
      }
    );
  }
  return windowAggregate(orders, day);
}

function dayMetrics(
  orders: OrderRow[],
  day: DayWindow,
  priorDay: DayWindow | null,
  connStatus: string,
  staleHours: number | null,
  dayAggs?: Record<string, WindowAggregate>,
  extra?: { returns?: Record<string, number>; traffic?: Record<string, number> },
): Partial<Record<OverlayMetricId, number | null>> {
  const y = resolveDay(orders, day, dayAggs);
  const p = priorDay
    ? resolveDay(orders, priorDay, dayAggs)
    : {
        orderCount: 0,
        ordersWithTotal: 0,
        sales: null,
        currencies: [] as string[],
        units: null,
        ordersWithUnits: 0,
        unshipped: 0,
        canceled: 0,
        aov: null,
      };
  const health = buildBusinessHealth({
    yesterday: y,
    prior: p,
    inventoryZero: null,
    inventorySample: 0,
    connectionStatus: connStatus,
    staleHours,
    hrefs: { orders: '#', inventory: '#', connect: '#' },
  });

  return {
    gmv: y.sales,
    orders: y.orderCount,
    units: y.units,
    aov: y.aov,
    asp: aspOf(y),
    health: health.overall,
    confidence: health.confidence,
    returns: extra?.returns?.[day.isoFrom] ?? null,
    traffic: extra?.traffic?.[day.isoFrom] ?? null,
  };
}

export function buildOverlayModel(input: {
  connectionId: string | null;
  connectionStatus: string;
  current: DayWindow;
  prior: DayWindow | null;
  orders: OrderRow[];
  dayAggs?: Record<string, WindowAggregate>;
  truncated: boolean;
  staleHours: number | null;
  error: string | null;
  source?: string;
  currency?: string;
  extraDay?: { returns?: Record<string, number>; traffic?: Record<string, number> };
}): MasterOverlayModel {
  const days = daysInWindow(input.current);
  const priorDays = input.prior ? daysInWindow(input.prior) : [];
  const points: OverlayPoint[] = days.map((day, index) => {
    const priorDay = priorDays[index] ?? null;
    // prev calendar day for health DoD within series
    const prevForHealth =
      index > 0
        ? days[index - 1]
        : priorDay;

    return {
      index,
      iso: day.isoFrom,
      label: day.label,
      priorIso: priorDay?.isoFrom,
      values: dayMetrics(
        input.orders,
        day,
        prevForHealth,
        input.connectionStatus,
        input.staleHours,
        input.dayAggs,
        input.extraDay,
      ),
      priorValues: priorDay
        ? dayMetrics(
            input.orders,
            priorDay,
            index > 0 ? priorDays[index - 1] ?? null : null,
            input.connectionStatus,
            input.staleHours,
            input.dayAggs,
            input.extraDay,
          )
        : {},
    };
  });

  let currency = input.currency?.toUpperCase();
  if (!currency) {
    for (const o of input.orders) {
      if (o.currency) {
        currency = o.currency.toUpperCase();
        break;
      }
    }
  }

  const hasReturns = Boolean(
    input.extraDay?.returns && Object.keys(input.extraDay.returns).length > 0,
  );
  const metrics = OVERLAY_METRICS.map((m) =>
    m.id === 'returns' && hasReturns
      ? { ...m, available: true, disabledReason: undefined, defaultOn: false }
      : m,
  );

  return {
    loadedAt: new Date().toISOString(),
    connectionId: input.connectionId,
    periodLabel: input.current.label,
    priorLabel: input.prior?.label ?? null,
    from: input.current.isoFrom,
    to: input.current.isoTo,
    currency,
    truncated: input.truncated,
    points,
    metrics,
    error: input.error,
    sourceNote: input.source
      ? `Orders source: ${input.source}. Daily buckets from Gate A item-price / order items / units.`
      : 'Daily buckets from Gate A when All Orders lines exist.',
  };
}

export function defaultEnabledIds(): OverlayMetricId[] {
  return OVERLAY_METRICS.filter((m) => m.available && m.defaultOn).map((m) => m.id);
}

export function exportOverlayCsv(
  model: MasterOverlayModel,
  enabled: OverlayMetricId[],
  compare: boolean,
): string {
  const active = model.metrics.filter((m) => m.available && enabled.includes(m.id));
  const headers = ['date', ...active.map((m) => m.id)];
  if (compare) headers.push(...active.map((m) => `${m.id}_prior`));
  const lines = [headers.join(',')];
  for (const p of model.points) {
    const row = [p.iso, ...active.map((m) => {
      const v = p.values[m.id];
      return v == null ? '' : String(v);
    })];
    if (compare) {
      row.push(
        ...active.map((m) => {
          const v = p.priorValues[m.id];
          return v == null ? '' : String(v);
        }),
      );
    }
    lines.push(row.join(','));
  }
  return lines.join('\n');
}
